import { shouldUseRTEHook } from '@pickleballinc/ui/lib/rte-helpers';
import clsx from 'clsx';
import { Fragment } from 'react';

import { publicEnv } from '@/lib/env';
import { ButtonsContainer } from '@/modules/homepage/ButtonsContainer/ButtonsContainer';
import { RenderCarousel } from '@/modules/homepage/RenderCarousel/RenderCarousel';
import { RenderExternalNews } from '@/modules/homepage/RenderExternalNews/RenderExternalNews';
import { RenderImage } from '@/modules/homepage/RenderImage/RenderImage';
import { RenderMatchCardV3 } from '@/modules/homepage/RenderMatchCardV3/RenderMatchCardV3';
import { RenderMatchCardV3Live } from '@/modules/homepage/RenderMatchCardV3/RenderMatchCardV3Live';
import { RenderMatches } from '@/modules/homepage/RenderMatches/RenderMatches';
import { RenderMatchesLive } from '@/modules/homepage/RenderMatches/RenderMatchesLive';
import { RenderNews } from '@/modules/homepage/RenderNews/RenderNews';
import { RenderResultCard } from '@/modules/homepage/RenderResultCard/RenderResultCard';
import { RenderResultCardLive } from '@/modules/homepage/RenderResultCard/RenderResultCardLive';
import { RenderVideo } from '@/modules/homepage/RenderVideo/RenderVideo';
import { TourTitle } from '@/modules/homepage/TourTitle/TourTitle';
import { type BlockMetadataInterace, BlockWidgetTypes } from '@/types/homepage.types';

export const RenderBlock = ({
  blockFooter,
  blockComponents,
  blockHeader,
}: BlockMetadataInterace) => {
  const blockContainerClasses = clsx(
    'flex flex-col overflow-hidden rounded-lg bg-white shadow-sm',
    {
      'gap-4 sm:gap-6': blockFooter || blockHeader,
      'pb-4 sm:pb-6': blockFooter,
      'pt-4 sm:pt-6': blockFooter && !blockHeader && blockComponents.length === 0,
    }
  );
  const blockComponentsContainerClasses = clsx('flex flex-col', {
    'gap-4 sm:gap-6': blockComponents.length > 1,
    'py-4 sm:py-6 px-4 sm:px-6': blockComponents.length > 0 && !blockFooter && !blockHeader,
    'px-4 sm:px-6': blockFooter || blockHeader,
    'pb-4 sm:pb-6': blockHeader && !blockFooter,
    'pt-4 sm:pt-6': !blockHeader && blockFooter,
  });

  return (
    <div className={blockContainerClasses}>
      {blockHeader ? (
        <TourTitle
          title={blockHeader.title}
          altTitle={blockHeader.altTitle}
          imagePosition={
            blockHeader.headerType === 'type-a'
              ? 'left'
              : blockHeader.headerType === 'type-b'
                ? 'right'
                : 'no-image'
          }
          image={blockHeader.imageURL}
          imageAltText={blockHeader.imageAltText}
          backgroundColor={blockHeader.backgroundColor}
          link={blockHeader.link}
        />
      ) : null}
      {blockComponents.length > 0 && (
        <div className={blockComponentsContainerClasses}>
          {blockComponents.map((blockComponent) => {
            return (
              <Fragment key={blockComponent.id}>
                {blockComponent.type === BlockWidgetTypes.NEWS && blockComponent.prefetchedData ? (
                  <RenderNews
                    newsType={blockComponent.newsType}
                    {...blockComponent.prefetchedData}
                  />
                ) : blockComponent.type === BlockWidgetTypes.VIDEO ? (
                  <RenderVideo {...blockComponent} />
                ) : blockComponent.type === BlockWidgetTypes.IMAGE ? (
                  <RenderImage {...blockComponent} />
                ) : blockComponent.type === BlockWidgetTypes.MATCH &&
                  blockComponent.prefetchedData ? (
                  shouldUseRTEHook(
                    blockComponent.prefetchedData,
                    publicEnv.NEXT_PUBLIC_RTE_ENABLED
                  ) ? (
                    <RenderMatchesLive
                      title={blockComponent.title}
                      initialMatches={blockComponent.prefetchedData}
                    />
                  ) : (
                    <RenderMatches
                      title={blockComponent.title}
                      initialMatches={blockComponent.prefetchedData}
                    />
                  )
                ) : blockComponent.type === BlockWidgetTypes.RESULT &&
                  blockComponent.prefetchedData ? (
                  shouldUseRTEHook(
                    blockComponent.prefetchedData,
                    publicEnv.NEXT_PUBLIC_RTE_ENABLED
                  ) ? (
                    <RenderResultCardLive initialMatches={blockComponent.prefetchedData} />
                  ) : (
                    <RenderResultCard initialMatches={blockComponent.prefetchedData} />
                  )
                ) : blockComponent.type === BlockWidgetTypes.EXTERNAL_NEWS ? (
                  <RenderExternalNews
                    url={blockComponent.url}
                    newsType={blockComponent.newsType}
                    {...(blockComponent.prefetchedData
                      ? blockComponent.prefetchedData
                      : blockComponent)}
                  />
                ) : blockComponent.type === BlockWidgetTypes.CAROUSEL &&
                  blockComponent.prefetchedData ? (
                  <RenderCarousel carouselNews={blockComponent.prefetchedData} />
                ) : blockComponent.type === BlockWidgetTypes.MATCHV3 &&
                  blockComponent.prefetchedData ? (
                  shouldUseRTEHook(
                    blockComponent.prefetchedData,
                    publicEnv.NEXT_PUBLIC_RTE_ENABLED
                  ) ? (
                    <RenderMatchCardV3Live initialMatches={blockComponent.prefetchedData} />
                  ) : (
                    <RenderMatchCardV3 initialMatches={blockComponent.prefetchedData} />
                  )
                ) : null}
              </Fragment>
            );
          })}
        </div>
      )}
      {blockFooter ? <ButtonsContainer buttons={blockFooter.buttons} /> : null}
    </div>
  );
};
